import { makeAutoObservable, runInAction } from "mobx";
import { BrokerService, getBrokerProfile } from "./BrokerService";

/**
 * Lazily caches the current broker's team members (id -> member) so that any
 * `createdByBrokerAdminId` reference can be rendered as a readable name via the
 * <BrokerAdminLabel /> component — without each row firing its own request.
 */
class BrokerAdminStore {
  byId = {};
  loading = false;
  loaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  setAdmins(list) {
    const map = {};
    (list || []).forEach((admin) => {
      const id = admin?._id || admin?.id || admin?.brokerAdminId;
      if (id) map[String(id)] = admin;
    });
    this.byId = map;
    this.loaded = true;
  }

  async ensureLoaded() {
    if (this.loaded || this.loading) return;
    // The broker-admin grid read is scoped to the caller's own broker, so any
    // broker admin can load the team to resolve teammate names (note authors,
    // assignees, …). Without a brokerId there is nothing to scope to.
    const profile = getBrokerProfile();
    if (!profile?.brokerId) {
      this.loaded = true;
      return;
    }
    this.loading = true;
    try {
      const response = await BrokerService.team(profile.brokerId);
      const payload = response?.data || {};
      const admins = payload.rows || payload.administrators || payload.broker?.administrators || [];
      runInAction(() => this.setAdmins(admins));
    } catch (error) {
      // Leave unloaded; labels fall back to a muted placeholder.
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  getName(id) {
    if (!id) return "";
    const admin = this.byId[String(id)];
    return admin?.name || admin?.email || "";
  }
}

export const brokerAdminStore = new BrokerAdminStore();

export default brokerAdminStore;
